

/**
 * Semaphore for scarce browser resources (video decoders, large downloads).
 *
 * acquire() blocks when activeIds.size >= maxConcurrent; release() drains the FIFO queue.
 * Campaign grid uses campaignPreviewLoadGate.js (max 4, release after first frame).
 */

export class ResourceLoadGate {
  /**
   * @param {number} maxConcurrent
   */
  constructor(maxConcurrent = 2) {
    this.maxConcurrent = maxConcurrent;
    /** @type {Set<string>} */
    this.activeIds = new Set();
    /** @type {{ id: string, resolve: () => void }[]} */
    this.queue = [];
  }

  /**
   * @param {string} resourceId
   * @returns {Promise<void>}
   */
  async acquire(resourceId) {
    if (!resourceId) return;
    if (this.activeIds.has(resourceId) || this.activeIds.size < this.maxConcurrent) {
      this.activeIds.add(resourceId);
      return;
    }
    await new Promise((resolve) => {
      this.queue.push({ id: resourceId, resolve });
    });
  }

  /**
   * @param {string} resourceId
   */
  release(resourceId) {
    if (!resourceId) return;
    this.activeIds.delete(resourceId);
    this.#drainQueue();
  }

  #drainQueue() {
    while (this.queue.length > 0 && this.activeIds.size < this.maxConcurrent) {
      const next = this.queue.shift();
      if (!next) break;
      if (this.activeIds.has(next.id)) {
        next.resolve();
        continue;
      }
      this.activeIds.add(next.id);
      next.resolve();
    }
  }

  reset() {
    this.activeIds.clear();
    this.queue.length = 0;
  }

  getStats() {
    return {
      active: this.activeIds.size,
      max: this.maxConcurrent,
      waiting: this.queue.length,
    };
  }
}
